import { oc } from "@orpc/contract";
import {
  SheetNameInputSchema,
  WorkbookSheetsResponseFields,
} from "../accounts-receivable/workbook-preview.contract";
import { z } from "zod";

/**
 * Supplier part-number → product mapping shown on /supplier-product-matches.
 * Backed by ap_supplier_products rows that carry supplierFreeform terms.
 */
export const SupplierProductMatchSchema = z.object({
  id: z.uuid(),
  supplierId: z.uuid(),
  productId: z.uuid(),
  productVariantId: z.uuid(),

  supplierDescription: z.string().nullable(),
  supplierMainIdentifier: z.string().nullable(),
  supplierSku: z.string().nullable(),
  supplierUpc: z.string().nullable(),
  supplierPlu: z.string().nullable(),
  supplierEan: z.string().nullable(),
  supplierGtin: z.string().nullable(),
  supplierAsin: z.string().nullable(),
  supplierMpn: z.string().nullable(),
  supplierIsbn: z.string().nullable(),
  supplierNsn: z.string().nullable(),
  supplierFreeform: z.array(z.string()).nullable(),

  supplier: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  product: z.object({
    id: z.uuid(),
    name: z.string(),
  }),
  productVariant: z.object({
    id: z.uuid(),
    name: z.string(),
    sku: z.string().nullable(),
    active: z.boolean(),
  }),

  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
});

export const ListSupplierProductMatchesQuerySchema = z.object({
  supplierId: z.uuid().optional(),
  productId: z.uuid().optional(),
  limit: z.coerce.number().int().min(1).max(100).prefault(25),
  offset: z.coerce.number().int().min(0).prefault(0),
});

export const ListSupplierProductMatchesResponseSchema = z.object({
  supplierProductMatches: z.array(SupplierProductMatchSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
  }),
});

const SupplierProductOptionalFieldsSchema = z.object({
  supplierDescription: z.string().nullable().optional(),
  supplierMainIdentifier: z.string().nullable().optional(),
  supplierSku: z.string().nullable().optional(),
  supplierUpc: z.string().nullable().optional(),
  supplierPlu: z.string().nullable().optional(),
  supplierEan: z.string().nullable().optional(),
  supplierGtin: z.string().nullable().optional(),
  supplierAsin: z.string().nullable().optional(),
  supplierMpn: z.string().nullable().optional(),
  supplierIsbn: z.string().nullable().optional(),
  supplierNsn: z.string().nullable().optional(),
});

export const CreateSupplierProductMatchInputSchema =
  SupplierProductOptionalFieldsSchema.extend({
    supplierId: z.uuid(),
    productVariantId: z.uuid(),
    supplierFreeform: z.array(z.string()).min(1),
  });

export const CreateSupplierProductMatchResponseSchema = z.object({
  supplierProductId: z.uuid(),
  didCreate: z.boolean(),
});

export const UpdateSupplierProductMatchInputSchema =
  SupplierProductOptionalFieldsSchema.extend({
    supplierProductId: z.uuid(),
    productVariantId: z.uuid().optional(),
    supplierFreeform: z.array(z.string()).nullable().optional(),
  });

export const UpdateSupplierProductMatchResponseSchema = z.object({
  supplierProductId: z.uuid(),
});

export const DeleteSupplierProductMatchInputSchema = z.object({
  supplierProductId: z.uuid(),
});

export const DeleteSupplierProductMatchResponseSchema = z.object({
  success: z.boolean(),
});

export const PreviewSupplierProductUploadInputSchema = z.object({
  documentId: z.uuid(),
  sheetName: SheetNameInputSchema,
});

export const PreviewSupplierProductUploadResponseSchema = z.object({
  ...WorkbookSheetsResponseFields,
  headers: z.array(z.string()),
  sampleRows: z.array(z.array(z.string())),
  totalDataRows: z.number().int().min(0),
  suggestedSupplierPartColumn: z.number().int().min(0).nullable(),
  suggestedInternalPartColumn: z.number().int().min(0).nullable(),
});

export const BulkUploadSupplierProductMatchesInputSchema = z.object({
  supplierId: z.uuid(),
  documentId: z.uuid(),
  sheetName: SheetNameInputSchema,
  supplierPartColumnIndex: z.number().int().min(0),
  internalPartColumnIndex: z.number().int().min(0),
});

export const UnmatchedSupplierProductRowSchema = z.object({
  rowIndex: z.number().int(),
  supplierPart: z.string(),
  internalPart: z.string(),
});

export const BulkUploadSupplierProductMatchesResponseSchema = z.object({
  totalRows: z.number().int().min(0),
  createdCount: z.number().int().min(0),
  mergedCount: z.number().int().min(0),
  unmatchedCount: z.number().int().min(0),
  skippedRows: z.number().int().min(0),
  unmatched: z.array(UnmatchedSupplierProductRowSchema),
  unmatchedTruncated: z.boolean(),
});

export const supplierProductMatchesContracts = {
  list: oc
    .input(ListSupplierProductMatchesQuerySchema)
    .output(ListSupplierProductMatchesResponseSchema)
    .route({
      method: "GET",
      path: "/supplier-product-matches",
      summary: "List supplier product matches",
      description:
        "Lists paged mappings from supplier part numbers and terms to catalog product variants, with supplier and product names.",
    }),
  create: oc
    .input(CreateSupplierProductMatchInputSchema)
    .output(CreateSupplierProductMatchResponseSchema)
    .route({
      method: "POST",
      path: "/supplier-product-matches",
      summary: "Create a supplier product match",
      description:
        "Maps a supplier's part number and identifiers (SKU, UPC, description, etc.) to a catalog product variant, merging into an existing match when one exists.",
    }),
  update: oc
    .input(UpdateSupplierProductMatchInputSchema)
    .output(UpdateSupplierProductMatchResponseSchema)
    .route({
      method: "PUT",
      path: "/supplier-product-matches",
      summary: "Update a supplier product match",
      description:
        "Changes a supplier product match's mapped product variant, part-number terms, or supplier identifiers such as SKU, UPC, or description.",
    }),
  delete: oc
    .input(DeleteSupplierProductMatchInputSchema)
    .output(DeleteSupplierProductMatchResponseSchema)
    .route({
      method: "DELETE",
      path: "/supplier-product-matches",
      summary: "Delete a supplier product match",
      description:
        "Removes a mapping from supplier part numbers to a catalog product variant.",
    }),
  previewBulkUpload: oc
    .input(PreviewSupplierProductUploadInputSchema)
    .output(PreviewSupplierProductUploadResponseSchema)
    .route({
      method: "POST",
      path: "/supplier-product-matches/preview-upload",
      summary: "Preview a supplier product match spreadsheet",
      description:
        "Reads an uploaded spreadsheet sheet and returns headers, sample rows, row count, and suggested supplier-part and internal-part columns. Saves nothing.",
    }),
  bulkUpload: oc
    .input(BulkUploadSupplierProductMatchesInputSchema)
    .output(BulkUploadSupplierProductMatchesResponseSchema)
    .route({
      method: "POST",
      path: "/supplier-product-matches/bulk-upload",
      summary: "Import supplier product matches from spreadsheet",
      description:
        "Imports supplier-part to internal-part mappings for a supplier from an uploaded spreadsheet. Returns created, merged, skipped, and unmatched row counts.",
    }),
} as const;

export type SupplierProductMatch = z.infer<typeof SupplierProductMatchSchema>;
export type ListSupplierProductMatchesQuery = z.infer<
  typeof ListSupplierProductMatchesQuerySchema
>;
export type ListSupplierProductMatchesResponse = z.infer<
  typeof ListSupplierProductMatchesResponseSchema
>;
export type CreateSupplierProductMatchInput = z.infer<
  typeof CreateSupplierProductMatchInputSchema
>;
export type CreateSupplierProductMatchResponse = z.infer<
  typeof CreateSupplierProductMatchResponseSchema
>;
export type UpdateSupplierProductMatchInput = z.infer<
  typeof UpdateSupplierProductMatchInputSchema
>;
export type UpdateSupplierProductMatchResponse = z.infer<
  typeof UpdateSupplierProductMatchResponseSchema
>;
export type DeleteSupplierProductMatchInput = z.infer<
  typeof DeleteSupplierProductMatchInputSchema
>;
export type DeleteSupplierProductMatchResponse = z.infer<
  typeof DeleteSupplierProductMatchResponseSchema
>;
export type PreviewSupplierProductUploadInput = z.infer<
  typeof PreviewSupplierProductUploadInputSchema
>;
export type PreviewSupplierProductUploadResponse = z.infer<
  typeof PreviewSupplierProductUploadResponseSchema
>;
export type BulkUploadSupplierProductMatchesInput = z.infer<
  typeof BulkUploadSupplierProductMatchesInputSchema
>;
export type BulkUploadSupplierProductMatchesResponse = z.infer<
  typeof BulkUploadSupplierProductMatchesResponseSchema
>;
export type UnmatchedSupplierProductRow = z.infer<
  typeof UnmatchedSupplierProductRowSchema
>;
